import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { api, resolveMediaUrl } from "@/lib/api";
import {
  Bookmark, UserCircle2, Users, Sparkles, Bell, CreditCard, Shield, Wand2,
  Settings2, LifeBuoy, LogOut, Trash2, ChevronRight, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AccountSwitcher from "@/components/AccountSwitcher";
import { PAYMENTS_UI_ENABLED } from "@/lib/featureFlags";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/users/me");
      toast.success("Account deleted");
      await logout();
      navigate("/login");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // Profile (public) route: prefer username, fall back to user_id for legacy.
  const publicProfilePath = user?.username ? `/@${user.username}` : `/u/${user?.user_id || ""}`;

  const items = [
    { id: "profile",       label: "Profile",              desc: "Your public page · followers, posts, bio.",          icon: Users,        onClick: () => navigate(publicProfilePath) },
    { id: "saved",         label: "Saved",                desc: "Outfits, try-ons, and looks you bookmarked.",        icon: Bookmark,     onClick: () => navigate("/collections") },
    { id: "personal",      label: "Personal details",     desc: "Profile detail, name, age, body measurements.",      icon: UserCircle2,  onClick: () => navigate("/profile/personal-details") },
    { id: "identity",      label: "AI Identity & Style",  desc: "Photos, style preferences, body & color analysis.",  icon: Sparkles,     onClick: () => navigate("/profile/ai-identity") },
    { id: "stylist_studio",label: "Stylist studio",       desc: "Open a public stylist profile · earn from consultations.", icon: Wand2, onClick: () => navigate("/stylist-studio") },
    { id: "notifications", label: "Notifications",        desc: "Daily look reminders, follower alerts.",             icon: Bell,         onClick: () => navigate("/profile/notifications") },
    ...(PAYMENTS_UI_ENABLED() ? [
      { id: "plans",       label: "Plans & subscription", desc: "Free, Premium, billing history.",                    icon: CreditCard,   onClick: () => navigate("/plans") }
    ] : []),
    { id: "privacy",       label: "Privacy & Security",   desc: "Password, sessions, data controls.",                 icon: Shield,       onClick: () => navigate("/profile/privacy") },
    { id: "settings",      label: "App settings",         desc: "Theme, zoom, permissions.",                          icon: Settings2,    onClick: () => navigate("/profile/app-settings") },
    { id: "help",          label: "Help",                 desc: "FAQs, contact us, terms.",                           icon: LifeBuoy,     onClick: () => navigate("/profile/help") },
    { id: "logout",        label: "Log out",              desc: "Sign out of this device.",                           icon: LogOut,       onClick: handleLogout },
    { id: "delete",        label: "Delete account",       desc: "Permanently remove all your data.",                  icon: Trash2,       onClick: () => setConfirmDelete(true), danger: true },
  ];

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <p className="eyebrow flex items-center gap-2"><UserCircle2 size={12} /> Stylebay · Account</p>
      <h1 className="editorial-h1 mt-3">Hello,<br/>{user?.name?.split(" ")[0] || "Stylist"}.</h1>

      {/* Identity card — taps open the account switcher */}
      <button
        type="button"
        onClick={() => setSwitcherOpen(true)}
        data-testid="profile-hub-identity-card"
        className="mt-8 w-full flex items-center gap-4 p-4 border border-border hover:border-foreground transition-colors text-left"
      >
        <div className="h-14 w-14 border border-border overflow-hidden shrink-0 rounded-full">
          {user?.avatar_url
            ? <img src={resolveMediaUrl(user.avatar_url)} alt="" className="h-full w-full object-cover" />
            : user?.picture
              ? <img src={resolveMediaUrl(user.picture)} alt="" className="h-full w-full object-cover" />
              : <span className="text-xl font-medium flex items-center justify-center h-full bg-accent text-accent-foreground">{user?.name?.[0] || "S"}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-2xl leading-tight truncate">{user?.name || "Stylist"}</p>
          <p className="text-xs text-muted-foreground truncate">
            {user?.username ? `@${user.username}` : (user?.email || "—")}
          </p>
        </div>
        <ChevronRight size={18} className="text-muted-foreground shrink-0" />
      </button>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-2 px-1">
        Tap to switch or add another account
      </p>

      {/* Bio + quick facts — reflect the personal details the user has saved
          so they can see their entry actually stuck. */}
      {(user?.bio || user?.city || user?.occupation) && (
        <div className="mt-6 border border-border p-4 space-y-2" data-testid="profile-hub-bio-card">
          {user?.bio && (
            <p className="text-sm leading-relaxed" data-testid="profile-hub-bio">
              &ldquo;{user.bio}&rdquo;
            </p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {user?.occupation && <span>· {user.occupation}</span>}
            {(user?.city || user?.country) && (
              <span>· {[user.city, user.country].filter(Boolean).join(", ")}</span>
            )}
            {user?.body_type && <span>· {user.body_type}</span>}
          </div>
        </div>
      )}

      {/* Menu list */}
      <ul className="mt-8 divide-y divide-border border-y border-border" data-testid="profile-hub-list">
        {items.map((it) => (
          <li key={it.id}>
            <button
              type="button"
              data-testid={`profile-hub-${it.id}`}
              onClick={it.onClick}
              className={`w-full flex items-center gap-4 px-2 py-4 sm:py-5 text-left hover:bg-muted/50 transition-colors group ${
                it.danger ? "text-destructive" : ""
              }`}
            >
              <span className={`h-10 w-10 inline-flex items-center justify-center border border-border shrink-0 ${
                it.danger ? "border-destructive/40" : "group-hover:border-foreground"
              }`}>
                <it.icon size={18} strokeWidth={1.5} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-base font-medium leading-tight">{it.label}</span>
                <span className="block text-[12px] text-muted-foreground mt-0.5 leading-snug">{it.desc}</span>
              </span>
              <ChevronRight size={16} className="text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </li>
        ))}
      </ul>

      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-10 text-center">
        Stylebay · Atelier
      </p>

      {/* Account switcher dialog */}
      <AccountSwitcher open={switcherOpen} onOpenChange={setSwitcherOpen} />

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="rounded-none max-w-md" data-testid="profile-delete-dialog">
          <DialogHeader>
            <p className="eyebrow text-destructive">Permanent action</p>
            <DialogTitle className="font-serif text-3xl tracking-tight">
              Delete your Stylebay account?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Your wardrobe, saved looks, identity media, and community posts will be removed forever.
            This cannot be undone.
          </p>
          <DialogFooter className="gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              className="rounded-none uppercase tracking-[0.18em] text-[11px]"
              data-testid="profile-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-none uppercase tracking-[0.18em] text-[11px] bg-destructive hover:bg-destructive/90"
              data-testid="profile-delete-confirm"
            >
              {deleting ? <><Loader2 size={12} className="animate-spin mr-2" />Deleting</> : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
